import type {
  EGlobalSearchGate,
  EIntegrationProvider,
  IGlobalSearchTarget,
  IParsedProject,
} from "@frontend-shared/types";

export enum EGlobalSearchDestinationType {
  // NOTE: A route inside the SPA.
  NAVIGATE = "navigate",
  // NOTE: A page outside the app shell (a generated HTML page, the docs
  // portal), opened in its own tab.
  OPEN_WINDOW = "open_window",
  SWITCH_PROJECT = "switch_project",
  OPEN_NOTIFICATIONS = "open_notifications",
}

export interface IGlobalSearchNavigateDestination {
  type:
    | EGlobalSearchDestinationType.NAVIGATE
    | EGlobalSearchDestinationType.OPEN_WINDOW;
  path: string;
}

export interface IGlobalSearchSwitchProjectDestination {
  type: EGlobalSearchDestinationType.SWITCH_PROJECT;
  projectUuid: string;
}

export interface IGlobalSearchOpenNotificationsDestination {
  type: EGlobalSearchDestinationType.OPEN_NOTIFICATIONS;
}

export type TGlobalSearchDestination =
  | IGlobalSearchNavigateDestination
  | IGlobalSearchSwitchProjectDestination
  | IGlobalSearchOpenNotificationsDestination;

export interface IResolveGlobalSearchDestinationProps {
  target: IGlobalSearchTarget;
  // NOTE: The organization of the ACTIVE project — organization settings
  // targets from the static index carry none and open this one.
  activeOrganizationUuid: string | null;
  // NOTE: custom_integration:write — the builder route is write-only, so a
  // reader lands on the Integrations "Custom" tab instead.
  canEditCustomIntegrations: boolean;
}

export type TGlobalSearchGateState = Readonly<
  Record<EGlobalSearchGate, boolean>
>;

export interface IGlobalSearchPermissionInputs {
  hasPrivateAssistantRead: boolean;
  hasPublicAssistantRead: boolean;
  hasKnowledgeRead: boolean;
  hasModelRead: boolean;
  hasPromptLibraryRead: boolean;
  hasAllMembersAnalyticsRead: boolean;
  hasAnalyticsRead: boolean;
  hasProbeRead: boolean;
  hasAutomationRead: boolean;
  hasMarketplaceAutomationRead: boolean;
  hasDocRecognitionRead: boolean;
  hasIntegrationRead: boolean;
  hasCustomIntegrationRead: boolean;
  hasCustomIntegrationWrite: boolean;
  hasCustomIntegrationDelete: boolean;
  hasCallAssistantRead: boolean;
  hasAnySkillRead: boolean;
  hasSkillCatalogRead: boolean;
}

// NOTE: Deployment switches. Everything read from /app-config is false while it
// loads, which is exactly the "not yet known → closed" rule of the gate enum.
export interface IGlobalSearchFlagInputs {
  isOnPremise: boolean;
  isBillingDisabled: boolean;
  isAgentsEnabled: boolean;
  isAnonymizationLabEnabled: boolean;
  isDocRecognitionEnabled: boolean;
  isSkillsEnabled: boolean;
  isCustomIntegrationEnabled: boolean;
  isCallAssistantEnabled: boolean;
  isExternalApiEnabled: boolean;
  isUserMemoryEnabled: boolean;
  isSlackBotEnabled: boolean;
  isTeamsBotEnabled: boolean;
  isMatrixBotEnabled: boolean;
  isTelegramBotEnabled: boolean;
  isWhatsappBotEnabled: boolean;
  isTokenUsageEnabled: boolean;
  isTokenCostUsageEnabled: boolean;
  isTokenLimitsEnabled: boolean;
  isTtsEnabled: boolean;
  ttsVoiceCount: number;
  isSyncatorApiEnabled: boolean;
  isSiemExportEnabled: boolean;
  isBrandedPresentationsEnabled: boolean;
  isFinancialMaskingEnabled: boolean;
  isPiiCustomEntitiesEnabled: boolean;
  isPiiAdvancedRulesEnabled: boolean;
  isPiiIntentRoutingEnabled: boolean;
  isSamlSeatTiersEnabled: boolean;
  isDomainVerificationAvailable: boolean;
  // NOTE: The per-model request limits endpoint answered `isEnabled: true`.
  isModelTokenLimitsEnabled: boolean;
}

export interface IGlobalSearchOrganizationInputs {
  isPersonalProject: boolean;
  // NOTE: The active project's organization, resolved from /organizations;
  // false until that answer arrives.
  isOrganizationOwner: boolean;
  // NOTE: The projects of that organization with the caller's per-project
  // permissions (the settings shell reads the same list).
  organizationProjects: readonly IParsedProject[];
  // NOTE: null while the enabled list is unknown — the organization
  // Integration tab stays available then, as it does on the settings page.
  enabledIntegrations: readonly EIntegrationProvider[] | null;
}

export interface IBuildGlobalSearchGateStateProps {
  permissions: IGlobalSearchPermissionInputs;
  flags: IGlobalSearchFlagInputs;
  organization: IGlobalSearchOrganizationInputs;
  // NOTE: The user signs in with an email + password.
  hasPasswordSignIn: boolean;
}
